import React from 'react'
import { Button } from './components/buttons/Button'
import { Icon } from './Icon'
import { IMG } from './images'

const HERO_BANNERS = [
  { img: 'hero-bess', kicker: 'Battery Energy Storage', title: "Powering Tomorrow's Energy Infrastructure",
    sub: 'Advanced Battery Energy Storage Systems engineered for residential, commercial and utility-scale applications.',
    primary: 'Explore BESS Solutions', secondary: 'Talk To Experts', route: 'bess' },
  { img: 'hero-mobility', kicker: 'Industrial Mobility', title: 'High-Performance Batteries For Industrial Mobility',
    sub: 'Reliable lithium battery solutions for industrial vehicles, automation systems and next-generation mobility.',
    primary: 'View Battery Solutions', secondary: 'Request Consultation', route: 'mobility' },
  { img: 'hero-solar', kicker: 'Solar EPC', title: 'Engineering Sustainable Solar Infrastructure',
    sub: 'Complete EPC services — from design and engineering to commissioning and maintenance.',
    primary: 'Explore Solar EPC', secondary: 'Request Site Assessment', route: 'solar' },
  { img: 'hero-robotics', kicker: 'Robotics Integration', title: 'Robotics System Integration',
    sub: 'End-to-end robotic integration driving precision, productivity and operational efficiency.',
    primary: 'Explore Robotics', secondary: 'Book Consultation', route: 'robotics' },
  { img: 'hero-automation', kicker: 'Industrial Automation', title: 'Transforming Factories Through Automation',
    sub: 'Industry 4.0-ready automation solutions that optimize performance, quality and operational excellence.',
    primary: 'Automation Solutions', secondary: 'Schedule Demo', route: 'automation' },
  { img: 'hero-npd', kicker: 'New Product Development', title: 'Innovating The Next Generation Of Technology',
    sub: 'Research-driven product development for defence, aerospace, marine, railways and advanced energy systems.',
    primary: 'Explore R&D Capabilities', secondary: 'Discuss Your Project', route: 'npd' },
];

export function Hero({ onNavigate }) {
  const [i, setI] = React.useState(0);
  const [anim, setAnim] = React.useState(0);
  const [px, setPx] = React.useState({ x: 0, y: 0 });
  const timer = React.useRef(null);
  const DURATION = 6500;

  const go = React.useCallback((n) => {
    setI((prev) => (n + HERO_BANNERS.length) % HERO_BANNERS.length);
    setAnim((a) => a + 1);
  }, []);

  React.useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    timer.current = setInterval(() => go(i + 1), DURATION);
    return () => clearInterval(timer.current);
  }, [i, go]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPx({ x: ((e.clientX - r.left) / r.width - 0.5) * 2, y: ((e.clientY - r.top) / r.height - 0.5) * 2 });
  };

  const b = HERO_BANNERS[i];

  return (
    <section data-screen-label="Hero" onMouseMove={onMove}
      style={{ position: 'relative', overflow: 'hidden', height: 'calc(100vh - 56px)', minHeight: 580, background: '#050f1f' }}>

      {HERO_BANNERS.map((bn, idx) => (
        <div key={bn.img} aria-hidden={idx !== i} style={{
          position: 'absolute', inset: '-4%',
          backgroundImage: `url("${IMG(bn.img)}")`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: idx === i ? 1 : 0,
          transform: `scale(${idx === i ? 1.06 : 1}) translate(${px.x * -10}px, ${px.y * -8}px)`,
          transition: 'opacity 1.1s var(--ease-out), transform 6.5s linear',
        }} />
      ))}

      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(5,15,31,0.85) 0%, rgba(5,15,31,0.6) 42%, rgba(5,15,31,0.2) 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(5,15,31,0.65) 0%, rgba(5,15,31,0) 45%)' }} />

      <div className="adr-particles">
        {Array.from({ length: 22 }).map((_, k) => (
          <span key={k} style={{ left: `${(k * 41) % 100}%`, top: `${(k * 57) % 100}%`, animationDelay: `${(k % 7) * 0.8}s`, animationDuration: `${6 + (k % 5)}s` }} />
        ))}
      </div>

      <div style={{ position: 'relative', zIndex: 3, height: '100%', maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', display: 'flex', alignItems: 'center' }}>
        <div key={anim} className="adr-hero-content" style={{
          maxWidth: 680, background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)',
          backdropFilter: 'blur(var(--glass-blur))', WebkitBackdropFilter: 'blur(var(--glass-blur))',
          borderRadius: 'var(--radius-xl)', padding: 'clamp(1.25rem, 2vw, 1.85rem)', boxShadow: 'var(--shadow-xl)',
        }}>
          <span className="adr-h-kick" style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', marginBottom: 16,
            borderRadius: 'var(--radius-pill)', background: 'rgba(67,196,67,0.16)', border: '1px solid rgba(122,251,122,0.35)',
            fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-xs)', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--energy-bright)',
          }}>
            <Icon name="zap" style={{ width: 14, height: 14 }} /> {b.kicker}
          </span>
          <h1 className="adr-h-title" style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.9rem, 1.2rem + 2.6vw, 3rem)',
            lineHeight: 1.06, letterSpacing: '-0.02em', color: '#fff', margin: '0 0 12px',
          }}>{b.title}</h1>
          <p className="adr-h-sub" style={{ fontSize: 'clamp(0.95rem, 0.9rem + 0.3vw, 1.0625rem)', lineHeight: 1.5, color: 'rgba(255,255,255,0.82)', margin: '0 0 22px' }}>{b.sub}</p>
          <div className="adr-h-cta" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Button variant="energy" size="md" iconRight={<Icon name="arrow-right" style={{ width: 17, height: 17 }} />} onClick={() => onNavigate({ page: 'Service', service: b.route })}>{b.primary}</Button>
            <Button variant="glass" size="md" onClick={() => onNavigate('Contact')}>{b.secondary}</Button>
          </div>
        </div>
      </div>

      <button aria-label="Previous" onClick={() => go(i - 1)} className="adr-hero-arrow" style={{ left: 18 }}>
        <Icon name="chevron-left" style={{ width: 24, height: 24 }} />
      </button>
      <button aria-label="Next" onClick={() => go(i + 1)} className="adr-hero-arrow" style={{ right: 18 }}>
        <Icon name="chevron-right" style={{ width: 24, height: 24 }} />
      </button>

      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4 }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter) 26px', display: 'flex', alignItems: 'center', gap: 22 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', fontSize: 15 }}>
            <span style={{ color: 'var(--energy-bright)' }}>0{i + 1}</span>
            <span style={{ opacity: 0.5 }}> / 0{HERO_BANNERS.length}</span>
          </span>
          <div style={{ flex: 1, height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.18)', overflow: 'hidden' }}>
            <div key={anim} className="adr-hero-progress" style={{ height: '100%', background: 'var(--grad-energy)', width: '0%' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {HERO_BANNERS.map((_, idx) => (
              <button key={idx} aria-label={`Banner ${idx + 1}`} onClick={() => go(idx)} style={{
                width: idx === i ? 26 : 9, height: 9, borderRadius: 9, border: 'none', cursor: 'pointer', padding: 0,
                background: idx === i ? 'var(--energy)' : 'rgba(255,255,255,0.35)', transition: 'all var(--dur-med) var(--ease-out)',
              }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
